import { Info } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './ui/select';

interface CronSchedulerProps {
  frequency: string;
  hour: string;
  minute: string;
  daysOfWeek: string[];
  daysOfMonth: number[];
  disabled?: boolean;
  isLoading: boolean;
  onSave: (frequencyObject: {
    type: string;
    hour: string;
    minute: string;
    days_of_week: string[];
    days_of_month: number[];
  }) => void;
}

const CronScheduler = (p: CronSchedulerProps) => {
  // Convert the frequency to match the SelectItem values
  const getFrequencyValue = (freq: string) => {
    const mapping: { [key: string]: string } = {
      manual: 'Manual',
      everyday: 'EveryDay',
      weekly: 'Weekly',
      monthly: 'Monthly',
      //   'quarterly': 'Quarterly'
    };
    return mapping[freq.toLowerCase()] || '';
  };

  const [frequency, setFrequency] = useState(getFrequencyValue(p.frequency));
  const [time, setTime] = useState(
    `${p.hour.padStart(2, '0')}:${p.minute.padStart(2, '0')}`,
  );
  const [daysOfWeek, setDaysOfWeek] = useState(p.daysOfWeek);
  const [daysOfMonth, setDaysOfMonth] = useState<number[]>(p.daysOfMonth);

  // Add this useEffect to update state when props change
  useEffect(() => {
    setFrequency(getFrequencyValue(p.frequency));
    setTime(`${p.hour.padStart(2, '0')}:${p.minute.padStart(2, '0')}`);
    setDaysOfWeek(p.daysOfWeek);
    setDaysOfMonth(p.daysOfMonth);
  }, [p.frequency, p.hour, p.minute, p.daysOfWeek, p.daysOfMonth]);

  // Refs to store the previous state values
  const prevFrequency = useRef(p.frequency);
  const prevTime = useRef(`${p.hour}:${p.minute}`);
  const prevDaysOfWeek = useRef(p.daysOfWeek);
  const prevDaysOfMonth = useRef<number[]>(p.daysOfMonth);

  const handleSave = useCallback(() => {
    let [hour, minute] = time.split(':');
    let day_of_week = daysOfWeek;
    let day_of_month = daysOfMonth;
    let frequencyObject = {
      type: frequency,
      hour: hour,
      minute: minute,
      days_of_week: day_of_week,
      days_of_month: day_of_month,
    };
    // Call the onSave prop with the frequency object
    p.onSave(frequencyObject);
  }, [time, daysOfWeek, daysOfMonth, frequency, p]);

  //   useEffect(() => {
  //     // Function to compare arrays
  //     const arraysEqual = (a: unknown[], b: unknown[]) => JSON.stringify(a) === JSON.stringify(b);

  //     // Check if any value is different from its previous value
  //     if (
  //       frequency !== prevFrequency.current ||
  //       time !== prevTime.current ||
  //       !arraysEqual(daysOfWeek, prevDaysOfWeek.current) ||
  //       !arraysEqual(daysOfMonth, prevDaysOfMonth.current)
  //     ) {
  //     //   handleSave();
  //     }

  //     // Update refs with current state
  //     prevFrequency.current = frequency;
  //     prevTime.current = time;
  //     prevDaysOfWeek.current = daysOfWeek;
  //     prevDaysOfMonth.current = daysOfMonth;
  //   }, [frequency, time, daysOfWeek, daysOfMonth, handleSave]);

  return (
    <>
      <div className="w-[350px]">
        <p className="text-sm font-semibold">Workflow frequency</p>
        <div className="mb-3 flex items-center">
          <Info className="mr-1 h-3 w-3 text-muted-foreground" />
          <p className="tracking-light text-xs text-muted-foreground">
            Indicates how frequently this workflow should run.
          </p>
        </div>

        <Select
          disabled={p.disabled}
          value={frequency || ''}
          onValueChange={value => {
            setFrequency(value);
            setDaysOfWeek([]);
            setDaysOfMonth([]);
          }}
        >
          <SelectTrigger className="w-[100%]">
            <SelectValue placeholder="Select frequency" />
          </SelectTrigger>
          <SelectContent className="w-[100%]">
            <SelectGroup>
              <SelectLabel>Frequency</SelectLabel>
              <SelectItem value="Manual">Manual</SelectItem>
              <SelectItem value="EveryDay">Daily</SelectItem>
              <SelectItem value="Weekly">Weekly</SelectItem>
              <SelectItem value="Monthly">Monthly</SelectItem>
              {/* <SelectItem value="Quarterly">Quarterly</SelectItem> */}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <div className="mt-4 w-[350px]">
        <p className="text-sm font-semibold">Execution Time</p>
        <div className="mb-3 flex items-center">
          <Info className="mr-1 h-3 w-3 text-muted-foreground" />
          <p className="tracking-light text-xs text-muted-foreground">
            Indicates the time at which the workflow will execute.
          </p>
        </div>
        <input
          className="h-9 w-[100%] rounded-md border border-gray-200 bg-white p-2.5"
          disabled={p.disabled}
          type="time"
          value={time}
          onChange={e => {
            setTime(e.target.value);
          }}
        />

        {frequency === 'Weekly' && (
          <div className="day-selector flex flex-wrap gap-2">
            {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map(
              (day, index) => (
                <div
                  key={index}
                  className={`day flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-gray-200 ${
                    daysOfWeek.includes(day) ? 'bg-red-500 text-white' : ''
                  } ${p.disabled ? 'cursor-not-allowed bg-gray-200 text-gray-500' : ''}`}
                  onClick={() => {
                    if (!p.disabled) {
                      if (daysOfWeek.includes(day))
                        setDaysOfWeek(prevDaysOfWeek =>
                          prevDaysOfWeek.filter(d => d !== day),
                        );
                      else
                        setDaysOfWeek(prevDaysOfWeek => [
                          ...prevDaysOfWeek,
                          day,
                        ]);
                    }
                  }}
                >
                  {day}
                </div>
              ),
            )}
          </div>
        )}

        {frequency === 'Monthly' && (
          <div className="day-selector flex flex-wrap gap-2">
            {Array.from({ length: 31 }).map((_, index) => (
              <div
                key={index}
                className={`day flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-gray-200 ${
                  daysOfMonth.includes(index + 1) ? 'bg-red-500 text-white' : ''
                } ${p.disabled ? 'cursor-not-allowed bg-gray-200 text-gray-500' : ''}`}
                onClick={() => {
                  if (!p.disabled) {
                    if (daysOfMonth.includes(index + 1)) {
                      setDaysOfMonth(prevDaysOfMonth =>
                        prevDaysOfMonth.filter(m => m !== index + 1),
                      );
                    } else {
                      setDaysOfMonth(prevDaysOfMonth => [
                        ...prevDaysOfMonth,
                        index + 1,
                      ]);
                    }
                  }
                }}
              >
                {index + 1}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          disabled={p.isLoading}
          onClick={() => {
            handleSave();
          }}
        >
          Save
        </Button>
      </div>
    </>
  );
};

export default CronScheduler;
